import React from 'react';
import { useTranslations } from '@lang';
import { useBodyScrollLock } from '@common/hooks/useBodyScrollLock';
import { useActiveMenu, MENU_IDS } from '@common/contexts/ActiveMenuContext';
import { ChevronDownIcon } from '@root/styles/icons';
import {
  LanguageSwitcherContainer,
  DrawerOverlay,
  DrawerPanel,
  Header,
  HeaderTitle,
  CloseButton,
  LanguageItem,
  TriggerButton,
} from './LanguageSwitcher.styles';
import languageOptions from '@common/components/Menu/Items/LanguageMenu/languageOptions';
import { changeLanguage } from './changeLanguage';

export type LanguageSwitcherProps = {
  availableLanguages: string[];
  currentLanguage: string;
};

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  availableLanguages,
  currentLanguage,
}) => {
  const { t } = useTranslations();
  const { activeMenuId, toggleMenu } = useActiveMenu();
  const isOpen = activeMenuId === MENU_IDS.LANGUAGE_SWITCHER;
  const targetRef = useBodyScrollLock(isOpen);

  if (!availableLanguages || availableLanguages.length <= 1) {
    return null;
  }

  const toggleDrawer = () => {
    toggleMenu(MENU_IDS.LANGUAGE_SWITCHER);
  };

  const handleLanguageSelect = (e: React.MouseEvent, langCode: string) => {
    e.stopPropagation();
    if (langCode === currentLanguage) {
      return;
    }
    changeLanguage(langCode);
  };

  const currentCountryCode = currentLanguage === 'en' ? 'gb' : currentLanguage;

  return (
    <LanguageSwitcherContainer>
      <TriggerButton
        data-testid="language-switcher-trigger"
        aria-label={t('aria.languageSwitcher') || 'Change language'}
        aria-expanded={isOpen}
        onClick={toggleDrawer}
      >
        <span className={`fi fi-${currentCountryCode}`}></span>
        {currentLanguage.toUpperCase()}
        <ChevronDownIcon className="chevron-icon" />
      </TriggerButton>
      <DrawerOverlay
        data-testid="language-switcher-drawer"
        isVisible={isOpen}
        onClick={toggleDrawer}
        ref={targetRef}
      >
        <DrawerPanel onClick={(e) => e.stopPropagation()}>
          <Header>
            <HeaderTitle>{t('languageSwitcher.selectLanguage')}</HeaderTitle>
            <CloseButton
              data-testid="language-switcher-close"
              onClick={toggleDrawer}
              aria-label="Close"
            >
              ×
            </CloseButton>
          </Header>
          {availableLanguages.map((langCode) => {
            const option = languageOptions[langCode];
            if (!option) {
              return null;
            }
            const countryCode = langCode === 'en' ? 'gb' : langCode;
            const isSelected = langCode === currentLanguage;
            return (
              <LanguageItem
                key={langCode}
                onClick={(e) => handleLanguageSelect(e, langCode)}
                className={isSelected ? 'selected' : ''}
              >
                <span className={`fi fi-${countryCode}`}></span>
                {option.label.text}
              </LanguageItem>
            );
          })}
        </DrawerPanel>
      </DrawerOverlay>
    </LanguageSwitcherContainer>
  );
};
